import { SELF_ALLOCATION_RETIRED } from "@/features/finances/selfAllocationFeature";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { ErrorPage } from "@/pages/ErrorPage";
import { MainLayout } from "@/layouts/MainLayout";
import { ProtectedLayout } from "@/layouts/ProtectedLayout";
import {
  AnyDLCModuleGuard,
  B2BInvoicesGuard,
  CeoOnlyGuard,
  ChatGuard,
  CorrespondenceRegisterGuard,
  DirectorOrCeoGuard,
  DLCModuleGuard,
  RoleGuard,
  TwoDriveGuard,
  SelfAllocationAdminGuard,
  SelfAllocationOwnerGuard,
  WorkloadSummaryGuard,
} from "./RoleGuard";
import { FinancePanelGuard } from "./FinancePanelGuard";
import { CEOGuard } from "./CEOGuard";
import { CEODevGuard } from "./CEODevGuard";
import { InternalCostFullGuard } from "./InternalCostFullGuard";
import { PricingCatalogGuard } from "./PricingCatalogGuard";
import {
  TechnicalDirectorGuard,
  ManagementCircleGuard,
  OrdersAccessGuard,
  TendersAccessGuard,
  ContractorAreaMapGuard,
  ContractorsAccessGuard,
  LeadsAccessGuard,
  ContractorsManageGuard,
  EopMailGuard,
} from "./TechnicalDirectorGuard";
import { EopMailboxPage } from "@/features/eop_mail/pages/EopMailboxPage";
import { MgmtBacklogPage } from "@/features/mgmt_backlog/pages/MgmtBacklogPage";
import { ZETenderAnalyticsGuard } from "./ZETenderAnalyticsGuard";
import { FridayGuard } from "./FridayGuard";
import { GroupFinancePage } from "@/features/group_finance/pages/GroupFinancePage";
import { CEOAccessPage } from "@/features/ceo/pages/CEOAccessPage";
import { AronPage } from "@/features/aron/pages/AronPage";
import { AronExpensesPage } from "@/features/aron/pages/AronExpensesPage";
import { AronKnowledgePage } from "@/features/aron/pages/AronKnowledgePage";
import { AronMetaPage } from "@/features/aron/pages/AronMetaPage";
import { AronAuditPage } from "@/features/aron/pages/AronAuditPage";
import { AronMailPage } from "@/features/aron_mail/pages/AronMailPage";
import { AronMailAliasesPage } from "@/features/aron_mail/pages/AronMailAliasesPage";
import { AronScheduledTasksPage } from "@/features/aron/pages/AronScheduledTasksPage";
import { EngAronReviewsPage } from "@/features/eng_aron/pages/EngAronReviewsPage";
import { EngAronReviewDetailPage } from "@/features/eng_aron/pages/EngAronReviewDetailPage";
import { EngAronKnowledgePage } from "@/features/eng_aron/pages/EngAronKnowledgePage";
import { EngAronFindingsPage } from "@/features/eng_aron/pages/EngAronFindingsPage";
import { EngAronChatPage } from "@/features/eng_aron/pages/EngAronChatPage";
import { PdfToolsPage } from "@/features/pdf_tools/pages/PdfToolsPage";
import FinAronDashboardPage from "@/features/fin_aron/pages/FinAronDashboardPage";
import FinAronPersonPage from "@/features/fin_aron/pages/FinAronPersonPage";
import FinAronOverheadPage from "@/features/fin_aron/pages/FinAronOverheadPage";
import FinAronAdvisorPage from "@/features/fin_aron/pages/FinAronAdvisorPage";
import FinAronKnowledgePage from "@/features/fin_aron/pages/FinAronKnowledgePage";
import EzFeedPage from "@/features/ezamowienia/pages/EzFeedPage";
import EzCandidatesPage from "@/features/ezamowienia/pages/EzCandidatesPage";
import EzKnowledgePage from "@/features/ezamowienia/pages/EzKnowledgePage";
import EzBenchmarksPage from "@/features/ezamowienia/pages/EzBenchmarksPage";
import EzChatPage from "@/features/ezamowienia/pages/EzChatPage";
import EzProceedingPage from "@/features/ezamowienia/pages/EzProceedingPage";

// Project docs feature
import { ClusterListPage } from "@/features/project_docs/pages/ClusterListPage";
import { ClusterDetailPage } from "@/features/project_docs/pages/ClusterDetailPage";
import { DocActivityPage } from "@/features/project_docs/pages/DocActivityPage";
import { DesignerActivityPage } from "@/features/project_docs/pages/DesignerActivityPage";

// Employee agreements feature
import { EmployeeAgreementsListPage } from "@/features/employee_agreements/pages/EmployeeAgreementsListPage";
import { EmployeeAgreementDetailPage } from "@/features/employee_agreements/pages/EmployeeAgreementDetailPage";
import { MyBillingPeriodsPage } from "@/features/employee_agreements/pages/MyBillingPeriodsPage";
import { EmployeeBillingOverviewPage } from "@/features/employee_agreements/pages/EmployeeBillingOverviewPage";

// Auth pages
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { SMSVerifyPage } from "@/features/auth/pages/SMSVerifyPage";

// Dashboard
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage";
import { DirectorApprovalsPage } from "@/features/director_approvals/pages/DirectorApprovalsPage";

// Orders feature
import { RequisitionsListPage } from "@/features/orders/pages/RequisitionsListPage";
import { RequisitionFormPage } from "@/features/orders/pages/RequisitionFormPage";
import { OrdersListPage } from "@/features/orders/pages/OrdersListPage";
import { OrderDetailPage } from "@/features/orders/pages/OrderDetailPage";
import { WeeklyReportsPage } from "@/features/orders/pages/WeeklyReportsPage";
import { WeeklyReportDetailPage } from "@/features/orders/pages/WeeklyReportDetailPage";

// Contractor panel
import { ContractorOrdersPage } from "@/features/orders/pages/ContractorOrdersPage";
import { ContractorTodoPage } from "@/features/orders/pages/ContractorTodoPage";
import { ContractorOrderDetailPage } from "@/features/orders/pages/ContractorOrderDetailPage";
import { ContractorInvoicesPage } from "@/features/invoices/pages/ContractorInvoicesPage";
import { ContractorAgreementsPage } from "@/features/contractors/pages/ContractorAgreementsPage";
import { ContractorLeavesPage } from "@/features/contractors/pages/ContractorLeavesPage";
import { ContractorAgreementDetailPage } from "@/features/contractors/pages/ContractorAgreementDetailPage";
import { ContractorProtocolsPage } from "@/features/deliveries/pages/ContractorProtocolsPage";
import { ContractorProtocolDetailPage } from "@/features/deliveries/pages/ContractorProtocolDetailPage";

// Enterprises
import { ProjectsListPage } from "@/features/enterprises/pages/ProjectsListPage";
import { ProjectDashboardPage } from "@/features/enterprises/pages/ProjectDashboardPage";
import { ProjectFormPage } from "@/features/enterprises/pages/ProjectFormPage";
import { ProjectEditPage } from "@/features/enterprises/pages/ProjectEditPage";
import { ProjectFinancePage } from "@/features/enterprises/pages/ProjectFinancePage";
import { ProjectGroupsListPage } from "@/features/enterprises/pages/ProjectGroupsListPage";
import { GroupForecastPage } from "@/features/finances/pages/GroupForecastPage";
import { SalaryReconciliationPage } from "@/features/finances/pages/SalaryReconciliationPage";
import { ContractorLayersPage } from "@/features/finances/pages/ContractorLayersPage";
import { OrderSimulationPage } from "@/features/finances/pages/OrderSimulationPage";
import { NonProjectSmearPage } from "@/features/finances/pages/NonProjectSmearPage";

// Power projects (DLC_01) + paper send (DLC_02)
import { PowerProjectsPortfolioPage } from "@/features/power_projects/pages/PowerProjectsPortfolioPage";
import { KpiChangeAnalysisPage } from "@/features/power_projects/pages/KpiChangeAnalysisPage";
import { GlobalParcelsPage } from "@/features/power_projects/pages/GlobalParcelsPage";
import { ZEMegaMapPage } from "@/features/power_projects/pages/ZEMegaMapPage";
import { ContractorMapPage } from "@/features/contractor_map/pages/ContractorMapPage";
import { ContractorProjectMapPage } from "@/features/contractor_map/pages/ContractorProjectMapPage";
import { ContractorLedProjectsPage } from "@/features/contractor_led/pages/ContractorLedProjectsPage";
import { LedProjectPage } from "@/features/contractor_led/pages/LedProjectPage";
import { RouteTutorialPage } from "@/features/contractor_led/pages/RouteTutorialPage";
import { WypisOrderPage } from "@/features/power_projects/pages/WypisOrderPage";
import { MzOrderPage } from "@/features/power_projects/pages/MzOrderPage";
import { MpzpOrderPage } from "@/features/power_projects/pages/MpzpOrderPage";
import { OfficeDocsPage } from "@/features/power_projects/pages/OfficeDocsPage";
import { WnioskiHubPage } from "@/features/power_projects/pages/wnioski/WnioskiHubPage";
import { WnioskiApprovalQueuePage } from "@/features/power_projects/pages/wnioski/WnioskiApprovalQueuePage";
import { APPLICATION_APPROVAL_ENABLED } from "@/features/power_projects/applicationApprovalFeature";
import { ApplicationPackagePage } from "@/features/power_projects/pages/wnioski/ApplicationPackagePage";
import { PoaRegistryPage } from "@/features/power_projects/pages/wnioski/PoaRegistryPage";
import { AdvisoryOversightPage } from "@/features/power_projects/advisor/AdvisoryOversightPage";
import { MaterialsOrderingPage } from "@/features/power_projects/pages/MaterialsOrderingPage";
import { ControlCallsPage } from "@/features/power_projects/pages/ControlCallsPage";
import { ProcessControlPage } from "@/features/power_projects/pages/ProcessControlPage";
import { RedirectWithSearch } from "./RedirectWithSearch";
import { PowerProjectWorkspacePage } from "@/features/power_projects/pages/PowerProjectWorkspacePage";
import { ConnectionRedirectPage } from "@/features/power_projects/pages/ConnectionRedirectPage";
import { FormatkiPlanPage } from "@/features/power_projects/pages/FormatkiPlanPage";
import { WpwMassPackagePage } from "@/features/power_projects/pages/WpwMassPackagePage";
import { CareRegistryPage } from "@/features/power_projects/pages/CareRegistryPage";
import { DesignersPage } from "@/features/power_projects/pages/DesignersPage";
import { ContactsPage } from "@/features/power_projects/pages/ContactsPage";
import { PresetsPage } from "@/features/power_projects/pages/PresetsPage";
import { DocGenerationPage } from "@/features/power_projects/pages/DocGenerationPage";
import { ZETendersPortfolioPage } from "@/features/ze_tenders/pages/ZETendersPortfolioPage";
import { ZETenderWorkspacePage } from "@/features/ze_tenders/pages/ZETenderWorkspacePage";
import { EmlImportPage } from "@/features/ze_tenders/pages/EmlImportPage";
import { ZECapabilitiesPage } from "@/features/ze_tenders/pages/ZECapabilitiesPage";
import { ZETenderAnalyticsPage } from "@/features/ze_tenders/pages/ZETenderAnalyticsPage";

// Tech calc feature (DLC_10 — Obliczenia techniczne)
import { TechCalcProjectsPage } from "@/features/tech_calc/pages/TechCalcProjectsPage";
import { TechCalcWorkspacePage } from "@/features/tech_calc/pages/TechCalcWorkspacePage";
import { TechCalcCardPage } from "@/features/tech_calc/pages/TechCalcCardPage";
import { NormCatalogPage } from "@/features/tech_calc/pages/NormCatalogPage";
import { PaperSendPage } from "@/features/paper_send/pages/PaperSendPage";
import { PrintOrdersPage } from "@/features/paper_send/pages/PrintOrdersPage";
import { OfficesPage } from "@/features/paper_send/pages/OfficesPage";
import { OutboxPage as EdoreczeniaOutboxPage } from "@/features/edoreczenia/pages/OutboxPage";
import { InboxPage as EdoreczeniaInboxPage } from "@/features/edoreczenia/pages/InboxPage";
import { MailboxesPage as EdoreczeniaMailboxesPage } from "@/features/edoreczenia/pages/MailboxesPage";

// Protocols
import { ProtocolsListPage } from "@/features/deliveries/pages/ProtocolsListPage";
import { ProtocolDetailPage } from "@/features/deliveries/pages/ProtocolDetailPage";

// Invoices — accounting + detail
import { AccountingInvoicesPage } from "@/features/invoices/pages/AccountingInvoicesPage";
import { InvoiceDetailPage } from "@/features/invoices/pages/InvoiceDetailPage";

// Requisition detail
import { RequisitionDetailPage } from "@/features/orders/pages/RequisitionDetailPage";

// Clients
import { ClientsListPage } from "@/features/clients/pages/ClientsListPage";
import { ClientDetailPage } from "@/features/clients/pages/ClientDetailPage";
import { ClientFormPage } from "@/features/clients/pages/ClientFormPage";
import { ClientBranchFormPage } from "@/features/clients/pages/ClientBranchFormPage";
import { ClientSubBranchFormPage } from "@/features/clients/pages/ClientSubBranchFormPage";

// Contractors
import { ContractorsListPage } from "@/features/contractors/pages/ContractorsListPage";
import { ContractorAreaMapPage } from "@/features/contractors/pages/ContractorAreaMapPage";
import { LeadsLayout } from "@/features/leads/components/LeadsLayout";
import { LeadsPage } from "@/features/leads/pages/LeadsPage";
import { LeadDetailPage } from "@/features/leads/pages/LeadDetailPage";
import { CampaignsPage } from "@/features/leads/pages/CampaignsPage";
import { LeadsQueuePage } from "@/features/leads/pages/LeadsQueuePage";
import { LeadsFunnelPage } from "@/features/leads/pages/LeadsFunnelPage";
import { CampaignDetailPage } from "@/features/leads/pages/CampaignDetailPage";
import { LeadSearchPage } from "@/features/leads/pages/LeadSearchPage";
import { LeadSearchDetailPage } from "@/features/leads/pages/LeadSearchDetailPage";
import { LeadTasksPage } from "@/features/leads/pages/LeadTasksPage";
import { UnsubscribePage } from "@/features/leads/pages/UnsubscribePage";
import { InboxPage } from "@/features/leads/pages/InboxPage";
import { LeadSuppressionPage } from "@/features/leads/pages/LeadSuppressionPage";
import { ScopeCategoriesPage } from "@/features/rfq/pages/ScopeCategoriesPage";
import { MyScopePage } from "@/features/rfq/pages/MyScopePage";
import { ScopeRequestsPage } from "@/features/rfq/pages/ScopeRequestsPage";
import { ProceedingsPage } from "@/features/rfq/pages/ProceedingsPage";
import { ProceedingPanelPage } from "@/features/rfq/pages/ProceedingPanelPage";
import { DeclarationLibraryPage } from "@/features/rfq/pages/DeclarationLibraryPage";
import { ContractTemplatesPage } from "@/features/rfq/pages/ContractTemplatesPage";
import { MyProceedingsPage } from "@/features/rfq/pages/MyProceedingsPage";
import { MyProceedingDetailPage } from "@/features/rfq/pages/MyProceedingDetailPage";
import { AccountRequestsPage } from "@/features/rfq/pages/AccountRequestsPage";
import {
  PublicProceedingDetailPage,
  PublicProceedingsPage,
} from "@/features/rfq/pages/PublicProceedingsPage";
import { ContractorDetailPage } from "@/features/contractors/pages/ContractorDetailPage";
import { KnowledgeNotesPage } from "@/features/contractors/pages/KnowledgeNotesPage";
import { ContractorFormPage } from "@/features/contractors/pages/ContractorFormPage";
import { AgreementsListPage } from "@/features/contractors/pages/AgreementsListPage";
import { AgreementDetailPage } from "@/features/contractors/pages/AgreementDetailPage";
import { PenaltiesPage } from "@/features/contractors/pages/PenaltiesPage";

// Finance global pages
import { ClientInvoicesPage } from "@/features/finances/pages/ClientInvoicesPage";
import { ClientInvoiceDetailPage } from "@/features/finances/pages/ClientInvoiceDetailPage";
import { ConsortiumPage } from "@/features/finances/pages/ConsortiumPage";
import { InternalCostsPage } from "@/features/finances/pages/InternalCostsPage";
import { SalaryCostsPage } from "@/features/finances/pages/SalaryCostsPage";
import { OrderCostMatrixPage } from "@/features/finances/pages/OrderCostMatrixPage";
import { EmployeeExpensesPage } from "@/features/finances/pages/EmployeeExpensesPage";
import { EmployeesPage } from "@/features/finances/pages/EmployeesPage";
import { HandoverPage } from "@/features/handover/pages/HandoverPage";
import { AccessesPage } from "@/features/finances/pages/AccessesPage";
import { DocumentTypeManagerPage } from "@/features/finances/pages/DocumentTypeManagerPage";
import { PlannedRevenuesPage } from "@/features/finances/pages/PlannedRevenuesPage";
import { PayrollActualsPage } from "@/features/finances/pages/PayrollActualsPage";

// Tenders feature
import { TendersListPage } from "@/features/tenders/pages/TendersListPage";
import { TenderDetailPage } from "@/features/tenders/pages/TenderDetailPage";
import { TenderNewPage } from "@/features/tenders/pages/TenderNewPage";
import { PricingCatalogPage } from "@/features/tenders/pages/PricingCatalogPage";
import { TenderScopePrefixesPage } from "@/features/tenders/pages/TenderScopePrefixesPage";
import { PricingNotificationsPage } from "@/features/tenders/pages/PricingNotificationsPage";

// Leaves
import { LeavesPage } from "@/features/leaves/pages/LeavesPage";
import { LeaveReviewPage } from "@/features/leaves/pages/LeaveReviewPage";
import { LeaveBalancesPage } from "@/features/leaves/pages/LeaveBalancesPage";
import { SickLeavesPage } from "@/features/leaves/pages/SickLeavesPage";
import { SickLeaveReviewPage } from "@/features/leaves/pages/SickLeaveReviewPage";

// Recruitment
import { RecruitmentPage } from "@/features/recruitment/pages/RecruitmentPage";

// Multisport
import { MultisportPage } from "@/features/multisport/pages/MultisportPage";
import { MultisportGuard } from "./MultisportGuard";
import { TechCalcNormsGuard } from "./TechCalcNormsGuard";

// Timesheets
import { TimesheetsPage } from "@/features/timesheets/pages/TimesheetsPage";
import { TimesheetReviewPage } from "@/features/timesheets/pages/TimesheetReviewPage";

// Supplies
import { SuppliesPage } from "@/features/supplies/pages/SuppliesPage";
import { AssetRegistryPage } from "@/features/asset_registry/pages/AssetRegistryPage";

// Fleet
import { FleetPage } from "@/features/fleet/pages/FleetPage";

// Couriers
import { CouriersPage } from "@/features/couriers/pages/CouriersPage";

// Schedule
import { SchedulePage } from "@/features/schedule/pages/SchedulePage";
import { RemoteWorkReviewPage } from "@/features/schedule/pages/RemoteWorkReviewPage";

// Settings
import { SettingsPage } from "@/features/settings/pages/SettingsPage";

// Dev Issues feature
import { DevIssuesListPage } from "@/features/dev_issues/pages/DevIssuesListPage";
import { DevIssueDetailPage } from "@/features/dev_issues/pages/DevIssueDetailPage";
import { DevMetricsPage } from "@/features/dev_issues/pages/DevMetricsPage";
import { DevActivityPage } from "@/features/dev_issues/pages/DevActivityPage";
import { DevAccountsPage } from "@/features/dev_issues/pages/DevAccountsPage";

// Repository feature
import { RepositoryPage } from "@/features/repository/pages/RepositoryPage";
// TwoDrive — personal file cloud
import { TwoDrivePage } from "@/features/two_drive/pages/TwoDrivePage";

// Instructions feature
import { InstructionsPage } from "@/features/instructions/pages/InstructionsPage";
import { WhatsNewPage } from "@/features/whats_new/pages/WhatsNewPage";
import { SystemMessagesPage } from "@/features/system_messages/pages/SystemMessagesPage";

// Background tasks panel
import { BgTasksPage } from "@/features/bg_tasks/pages/BgTasksPage";
import { MyBgTasksPage } from "@/features/bg_tasks/pages/MyBgTasksPage";

// Maintenance feature
import { MaintenancePage } from "@/features/maintenance/pages/MaintenancePage";
import { MaintenanceManagePage } from "@/features/maintenance/pages/MaintenanceManagePage";
import { CredentialsPage } from "@/features/maintenance/pages/CredentialsPage";
import { FileScansPage } from "@/features/maintenance/pages/FileScansPage";
import { PresetFunctionsDevPage } from "@/features/tenders/pages/PresetFunctionsDevPage";
import ExtensionsPage from "@/features/browser_extensions/pages/ExtensionsPage";
import { SmsLogPage } from "@/features/sms/pages/SmsLogPage";

// Tasks feature
import { TasksBoardPage } from "@/features/tasks/pages/TasksBoardPage";
import { TasksListPage } from "@/features/tasks/pages/TasksListPage";
import { MyTasksPage } from "@/features/tasks/pages/MyTasksPage";
import { WeeklyPlannerPage } from "@/features/tasks/pages/WeeklyPlannerPage";
import { EmployeeProgressPage } from "@/features/tasks/pages/EmployeeProgressPage";

// Official fees
import OfficialFeeRequestsPage from "@/features/official_fees/pages/OfficialFeeRequestsPage";
import MyB2BInvoicesPage from "@/features/b2b_invoices/pages/MyB2BInvoicesPage";
import B2BInvoiceCreatePage from "@/features/b2b_invoices/pages/B2BInvoiceCreatePage";
import B2BInvoiceDetailPage from "@/features/b2b_invoices/pages/B2BInvoiceDetailPage";
import AccountingB2BPage from "@/features/b2b_invoices/pages/AccountingB2BPage";
import SelfAllocationListPage from "@/features/finances/pages/SelfAllocationListPage";
import SelfAllocationDetailPage from "@/features/finances/pages/SelfAllocationDetailPage";
import SelfAllocationAdminPage from "@/features/finances/pages/SelfAllocationAdminPage";
import WorkloadSummaryPage from "@/features/finances/pages/WorkloadSummaryPage";
import OfficialFeeRequestDetailPage from "@/features/official_fees/pages/OfficialFeeRequestDetailPage";
import OfficialFeeRequestCreatePage from "@/features/official_fees/pages/OfficialFeeRequestCreatePage";
import OfficialFeeAddPage from "@/features/official_fees/pages/OfficialFeeAddPage";

// Estimating — kosztorys nakładczy → wartość przetargowa (SEKOCENBUD)
import { EstimateUploadPage } from "@/features/estimating/pages/EstimateUploadPage";
import { EstimateResultPage } from "@/features/estimating/pages/EstimateResultPage";
import { EstimateKnowledgePage } from "@/features/estimating/pages/EstimateKnowledgePage";
import { CustomPricesPage } from "@/features/estimating/pages/CustomPricesPage";
import { CatalogBrowserPage } from "@/features/estimating/pages/CatalogBrowserPage";
import { TakeoffRulesPage } from "@/features/estimating/pages/TakeoffRulesPage";

// GG — chat feature
import { GGPage } from "@/features/chat/pages/GGPage";

// Spotkania zdalne (meetings)
import { MeetingsPage } from "@/features/meetings/pages/MeetingsPage";
import { MeetingDetailPage } from "@/features/meetings/pages/MeetingDetailPage";
import { OccurrenceDetailPage } from "@/features/meetings/pages/OccurrenceDetailPage";
import { MeetingRoomPage } from "@/features/meetings/pages/MeetingRoomPage";
import { GuestMeetingPage } from "@/features/meetings/pages/GuestMeetingPage";

// Contacts
import { ContactsPage as ContactsDirectoryPage } from "@/features/contacts/pages/ContactsPage";
import { ContactsGuard } from "./ContactsGuard";

export const router = createBrowserRouter([
  {
    path: "/auth",
    element: <MainLayout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to="login" replace /> },
      { path: "login", element: <LoginPage />, handle: { title: "Logowanie" } },
      {
        path: "sms-verify",
        element: <SMSVerifyPage />,
        handle: { title: "Weryfikacja SMS" },
      },
    ],
  },
  // Publiczna strona gościa spotkania — POZA ProtectedLayout: token z URL jest
  // jedynym poświadczeniem, zero JWT i zero zapytań wymagających logowania.
  {
    path: "/meet/:token",
    element: <GuestMeetingPage />,
    errorElement: <ErrorPage />,
  },
  // Publiczna lista postępowań ofertowych — POZA ProtectedLayout. Ma ją zobaczyć
  // firma, której jeszcze nie znamy (i wyszukiwarka), więc przekierowanie na
  // logowanie przekreślałoby cały sens. Oferty nadal wymagają konta: przycisk
  // „Złóż ofertę" prowadzi do prośby o jego założenie.
  {
    path: "/postepowania",
    element: <PublicProceedingsPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/postepowania/:id",
    element: <PublicProceedingDetailPage />,
    errorElement: <ErrorPage />,
  },
  // Rezygnacja z kontaktu w module leadów — również POZA ProtectedLayout.
  // Adres trafia do zimnych maili, więc otwiera go firma, która nie ma u nas
  // konta; przekierowanie na logowanie odebrałoby jej jedyną drogę wyjścia.
  {
    path: "/rezygnacja/:token",
    element: <UnsubscribePage />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/",
    element: <ProtectedLayout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },

      // „Do zatwierdzenia" — zbiorczy widok decyzji dyrektora (druga droga obok modułów).
      {
        path: "do-zatwierdzenia",
        element: <DirectorOrCeoGuard />,
        children: [
          {
            index: true,
            element: <DirectorApprovalsPage />,
            handle: { title: "Do zatwierdzenia" },
          },
        ],
      },

      // Repository — all authenticated users (backend enforces per-role filtering)
      {
        path: "repository",
        element: <RepositoryPage />,
        handle: { title: "Repozytorium" },
      },

      // TwoDrive — personal file cloud. Pracownicy GK zawsze; konto podwykonawcy
      // wyłącznie z flagą od CEO — stąd TwoDriveGuard zamiast RoleGuard.
      {
        path: "two-drive",
        element: <TwoDriveGuard />,
        children: [
          {
            index: true,
            element: <TwoDrivePage />,
            handle: { title: "TwoDrive" },
          },
        ],
      },

      // GG — komunikator (real-time via Centrifugo). Pracownicy GK zawsze, konto
      // podwykonawcy wyłącznie z flagą od CEO — stąd ChatGuard zamiast RoleGuard.
      {
        path: "chat",
        element: <ChatGuard />,
        children: [
          { index: true, element: <GGPage />, handle: { title: "GG" } },
        ],
      },

      // Spotkania zdalne — wszyscy zalogowani (podwykonawcy widzą tylko swoje)
      {
        path: "meetings",
        children: [
          {
            index: true,
            element: <MeetingsPage />,
            handle: { title: "Spotkania" },
          },
          {
            path: "occurrences/:occurrenceId",
            element: <OccurrenceDetailPage />,
            handle: { title: "Spotkanie" },
          },
          {
            path: "room/:occurrenceId",
            element: <MeetingRoomPage />,
            handle: { title: "Pokój spotkania" },
          },
          {
            path: ":meetingId",
            element: <MeetingDetailPage />,
            handle: { title: "Spotkanie" },
          },
        ],
      },

      // Contacts directory — CEO, directors, coordinators, and users with has_contacts_access
      // (contractors included, limited on the backend to their picked branches)
      {
        path: "contacts",
        element: (
          <ContactsGuard>
            <ContactsDirectoryPage />
          </ContactsGuard>
        ),
        handle: { title: "Kontakty" },
      },

      // Instructions — all authenticated users (backend enforces access filtering)
      {
        path: "instructions",
        element: <InstructionsPage />,
        handle: { title: "Instrukcje" },
      },

      // Co nowego — everyone; contractors see only announcements targeting them.
      {
        path: "whats-new",
        element: <WhatsNewPage />,
        handle: { title: "Co nowego" },
      },

      // Wiadomości systemowe — strona nadawcy. Bramkuje się sama (dyrekcja),
      // tak jak backend; odbiorcy dostają wiadomość blokującym oknem.
      {
        path: "system-messages",
        element: <SystemMessagesPage />,
        handle: { title: "Wiadomości systemowe" },
      },

      // Invoice detail — accessible to all who can see invoices
      { path: "invoices/:id", element: <InvoiceDetailPage /> },

      // Employee expenses — all internal users
      {
        path: "expenses",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee"]}
          />
        ),
        children: [
          {
            index: true,
            element: <EmployeeExpensesPage />,
            handle: { title: "Wydatki" },
          },
        ],
      },

      // Tasks feature — all internal users + contractors with has_friday_access
      {
        path: "tasks",
        element: <FridayGuard />,
        children: [
          { index: true, element: <Navigate to="board" replace /> },
          {
            path: "board",
            element: <TasksBoardPage />,
            handle: { title: "Friday – tablica" },
          },
          {
            path: "list",
            element: <TasksListPage />,
            handle: { title: "Friday – lista" },
          },
          {
            path: "my",
            element: <MyTasksPage />,
            handle: { title: "Moje zadania" },
          },
          {
            path: "planner",
            element: <RoleGuard roles={["director", "coordinator"]} />,
            children: [
              {
                index: true,
                element: <WeeklyPlannerPage />,
                handle: { title: "Planer tygodniowy" },
              },
            ],
          },
          {
            path: "progress",
            element: <RoleGuard roles={["director"]} />,
            children: [
              {
                index: true,
                element: <EmployeeProgressPage />,
                handle: { title: "Progress zadań" },
              },
            ],
          },
        ],
      },

      // Employees (director, accountant, hr)
      {
        path: "employees",
        element: <RoleGuard roles={["director", "accountant", "hr"]} />,
        children: [
          {
            index: true,
            element: <EmployeesPage />,
            handle: { title: "Pracownicy" },
          },
          {
            path: "document-types",
            element: <DocumentTypeManagerPage />,
            handle: { title: "Typy dokumentów" },
          },
        ],
      },

      // Przekazanie obowiązków — dyrektor techniczny / CEO (lustro IsTechnicalDirector)
      {
        path: "przekazanie-obowiazkow",
        // ManagementCircleGuard, nie TechnicalDirectorGuard: ten drugi odsyła tylko
        // dyrektorów nietechnicznych i przepuszcza wszystkie pozostałe role.
        element: <ManagementCircleGuard />,
        children: [
          {
            index: true,
            element: <HandoverPage />,
            handle: { title: "Przekazanie obowiązków" },
          },
        ],
      },

      // Accesses (director, accountant, hr)
      {
        path: "accesses",
        element: <RoleGuard roles={["director", "accountant", "hr"]} />,
        children: [
          {
            index: true,
            element: <AccessesPage />,
            handle: { title: "Dostępy" },
          },
        ],
      },

      // Recruitment — director + hr (backend enforces branch scoping)
      {
        path: "recruitment",
        element: <RoleGuard roles={["director", "hr", "accountant"]} />,
        children: [
          {
            index: true,
            element: <RecruitmentPage />,
            handle: { title: "Rekrutacja" },
          },
        ],
      },

      // Multisport — hr / accountant / CEO / has_multisport_access
      {
        path: "multisport",
        element: <MultisportGuard />,
        children: [
          {
            index: true,
            element: <MultisportPage />,
            handle: { title: "Multisport" },
          },
        ],
      },

      // Schedule — all internal users
      {
        path: "schedule",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <SchedulePage />,
            handle: { title: "Harmonogram" },
          },
          {
            path: "review",
            element: <RoleGuard roles={["director", "hr", "accountant"]} />,
            children: [
              {
                index: true,
                element: <RemoteWorkReviewPage />,
                handle: { title: "Praca zdalna – akceptacja" },
              },
            ],
          },
        ],
      },

      // Leaves — employees submit, directors/HR review
      {
        path: "leaves",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          { index: true, element: <LeavesPage />, handle: { title: "Urlopy" } },
          {
            path: "review",
            element: (
              <RoleGuard
                roles={["director", "hr", "accountant", "coordinator"]}
              />
            ),
            children: [
              {
                index: true,
                element: <LeaveReviewPage />,
                handle: { title: "Urlopy – akceptacja" },
              },
            ],
          },
          {
            path: "balances",
            element: <RoleGuard roles={["director", "hr", "accountant"]} />,
            children: [
              {
                index: true,
                element: <LeaveBalancesPage />,
                handle: { title: "Bilanse urlopowe" },
              },
            ],
          },
        ],
      },

      // Sick leaves (L4) — separate from urlopy
      {
        path: "sick-leaves",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <SickLeavesPage />,
            handle: { title: "Zwolnienia L4" },
          },
          {
            path: "review",
            element: <RoleGuard roles={["director", "hr", "accountant"]} />,
            children: [
              {
                index: true,
                element: <SickLeaveReviewPage />,
                handle: { title: "Zwolnienia L4 – akceptacja" },
              },
            ],
          },
        ],
      },

      // Timesheets — employees submit, directors/accountants review
      {
        path: "timesheets",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <TimesheetsPage />,
            handle: { title: "Karty pracy" },
          },
          {
            path: "review",
            element: (
              <RoleGuard roles={["director", "accountant", "coordinator"]} />
            ),
            children: [
              {
                index: true,
                element: <TimesheetReviewPage />,
                handle: { title: "Karty pracy – akceptacja" },
              },
            ],
          },
        ],
      },

      // Supplies — all internal users
      {
        path: "supplies",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <SuppliesPage />,
            handle: { title: "Zapotrzebowania biurowe" },
          },
        ],
      },

      // Asset registry (Ewidencja majątku) — all internal users
      {
        path: "asset-registry",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <AssetRegistryPage />,
            handle: { title: "Ewidencja majątku" },
          },
        ],
      },

      // Fleet — all internal users
      {
        path: "fleet",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <FleetPage />,
            handle: { title: "Flota samochodów" },
          },
        ],
      },

      // Couriers — all internal users
      {
        path: "couriers",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <CouriersPage />,
            handle: { title: "Zamawianie kuriera" },
          },
        ],
      },

      // Official fees — all internal users + contractors (backend enforces per-role filtering)
      {
        path: "official-fees",
        element: (
          <RoleGuard
            roles={[
              "director",
              "coordinator",
              "accountant",
              "employee",
              "hr",
              "contractor",
            ]}
          />
        ),
        children: [
          {
            index: true,
            element: <OfficialFeeRequestsPage />,
            handle: { title: "Opłaty urzędowe" },
          },
          {
            path: "new",
            element: <OfficialFeeRequestCreatePage />,
            handle: { title: "Nowa opłata urzędowa" },
          },
          {
            path: "add",
            element: <OfficialFeeAddPage />,
            handle: { title: "Dodaj opłatę urzędową" },
          },
          { path: ":id", element: <OfficialFeeRequestDetailPage /> },
        ],
      },

      // B2B invoices — anyone on B2B contract (any role except contractor)
      // sees their own; CEO + accountant see all.
      // Backend enforces fine-grained access (CanCreateB2BInvoice / CanListB2BInvoices).
      {
        path: "b2b-invoices",
        element: <B2BInvoicesGuard />,
        children: [
          {
            index: true,
            element: <MyB2BInvoicesPage />,
            handle: { title: "Moje faktury B2B" },
          },
          {
            path: "new",
            element: <B2BInvoiceCreatePage />,
            handle: { title: "Nowa faktura B2B" },
          },
          { path: ":id", element: <B2BInvoiceDetailPage /> },
        ],
      },

      // Faktury B2B — jeden ujednolicony widok administracyjny dla CEO + Księgowego.
      // Stary URL /ceo/b2b-invoices przekierowujemy do /finances/b2b-invoices,
      // żeby zakładki/bookmarki nie zostały zerwane.
      {
        path: "ceo/b2b-invoices",
        element: <Navigate to="/finances/b2b-invoices" replace />,
      },
      {
        path: "finances/b2b-invoices",
        element: <RoleGuard roles={["director", "accountant"]} />,
        children: [
          {
            index: true,
            element: <AccountingB2BPage />,
            handle: { title: "Faktury B2B – księgowość" },
          },
        ],
      },

      // Samo-alokacja — pracownik z can_self_allocate
      {
        path: "finances/self-allocation",
        element: <SelfAllocationOwnerGuard />,
        children: [
          {
            index: true,
            element: <SelfAllocationListPage />,
            handle: { title: "Rozkład pracy per przedsięwzięcie" },
          },
          { path: ":id", element: <SelfAllocationDetailPage /> },
        ],
      },
      // Rozkład pracy – podsumowanie (CEO z finansami, dyrektor techniczny bez finansów)
      {
        path: "finances/workload-summary",
        element: <WorkloadSummaryGuard />,
        children: [
          {
            index: true,
            element: <WorkloadSummaryPage />,
            handle: { title: "Rozkład pracy – podsumowanie" },
          },
        ],
      },
      // Samo-alokacja — panel CEO
      {
        path: "finances/self-allocations-admin",
        element: <SelfAllocationAdminGuard />,
        children: [
          {
            index: true,
            element: <SelfAllocationAdminPage />,
            handle: {
              title: SELF_ALLOCATION_RETIRED
                ? "Rozkład pracy – archiwum wniosków"
                : "Rozkład pracy per przedsięwzięcie – administracja",
            },
          },
        ],
      },

      // Payroll actuals — CEO (director + is_ceo), HR (write), Accountant (read)
      {
        path: "finances/payroll-actuals",
        element: <RoleGuard roles={["director", "hr", "accountant"]} />,
        children: [
          {
            index: true,
            element: <PayrollActualsPage />,
            handle: { title: "Płace – urealnienie" },
          },
        ],
      },

      // Finances — mixed access: some sub-routes allowed for non-tech directors (costs, salary)
      {
        path: "finances",
        element: (
          <RoleGuard roles={["director", "coordinator", "accountant"]} />
        ),
        children: [
          // Non-tech directors can view (branch-filtered on backend)
          {
            element: <InternalCostFullGuard />,
            children: [
              {
                path: "costs",
                element: <InternalCostsPage />,
                handle: { title: "Koszty wewnętrzne" },
              },
            ],
          },
          {
            path: "salary",
            element: <SalaryCostsPage />,
            handle: { title: "Koszty płac" },
          },
          // Technical directors only
          {
            element: <TechnicalDirectorGuard />,
            children: [
              {
                path: "invoices-prz",
                element: <ClientInvoicesPage />,
                handle: { title: "Faktury PRZ" },
              },
              {
                path: "invoices-prz/:id",
                element: <ClientInvoiceDetailPage />,
              },
              {
                path: "consortium",
                element: <ConsortiumPage />,
                handle: { title: "Konsorcja" },
              },
              {
                path: "planned-revenues",
                element: <PlannedRevenuesPage />,
                handle: { title: "Planowane przychody" },
              },
              {
                path: "order-costs",
                element: <OrderCostMatrixPage />,
                handle: { title: "Macierz kosztów zamówień" },
              },
              {
                path: "project-groups",
                element: (
                  <CEOGuard>
                    <ProjectGroupsListPage />
                  </CEOGuard>
                ),
                handle: { title: "Grupy przedsięwzięć" },
              },
              {
                path: "group-forecast",
                element: (
                  <CEOGuard>
                    <GroupForecastPage />
                  </CEOGuard>
                ),
                handle: { title: "Prognoza obłożenia" },
              },
              {
                path: "salary-reconciliation",
                element: (
                  <CEOGuard>
                    <SalaryReconciliationPage />
                  </CEOGuard>
                ),
                handle: { title: "Porównanie podziału płac" },
              },
              {
                path: "contractor-layers",
                element: (
                  <CEOGuard>
                    <ContractorLayersPage />
                  </CEOGuard>
                ),
                handle: { title: "Koszty podwykonawcze wg statusu" },
              },
              {
                path: "order-simulations",
                element: (
                  <CEOGuard>
                    <OrderSimulationPage />
                  </CEOGuard>
                ),
                handle: { title: "Symulacja zlecenia / przetargu" },
              },
              {
                path: "non-project-smear",
                element: (
                  <CEOGuard>
                    <NonProjectSmearPage />
                  </CEOGuard>
                ),
                handle: { title: "Podział kosztów obsługi firmy" },
              },
            ],
          },
        ],
      },

      // Management backlog — strict circle: technical directors + CEO only
      {
        element: <ManagementCircleGuard />,
        children: [
          {
            path: "mgmt-backlog",
            element: <MgmtBacklogPage />,
            handle: { title: "Backlog zarządczy" },
          },
        ],
      },

      // Technical issues — all authenticated users can report; devs/CEO see all
      {
        path: "dev-issues",
        children: [
          {
            index: true,
            element: <DevIssuesListPage />,
            handle: { title: "Zgłoszenia techniczne" },
          },
          { path: ":id", element: <DevIssueDetailPage /> },
        ],
      },

      // Weekly report detail — reachable from notification links by both GK
      // and contractor; backend enforces access. Must be a sibling of /orders
      // (technical-director-gated) so contractors aren't blocked.
      {
        path: "orders/:orderId/weekly-reports/:reportId",
        element: <WeeklyReportDetailPage />,
        handle: { title: "Raport tygodniowy" },
      },

      // Contractor panel (c)
      {
        path: "contractor",
        element: <RoleGuard roles={["contractor"]} />,
        children: [
          { index: true, element: <Navigate to="orders" replace /> },
          {
            path: "orders",
            element: <ContractorOrdersPage />,
            handle: { title: "Moje zamówienia" },
          },
          { path: "orders/:id", element: <ContractorOrderDetailPage /> },
          {
            path: "map",
            element: <ContractorMapPage />,
            handle: { title: "Mapa zamówień" },
          },
          { path: "map/:connectionId", element: <ContractorProjectMapPage /> },
          // Projekty ZE prowadzone przez firmę (widok nadzorczy, apps.contractor_led).
          {
            path: "projects",
            element: <ContractorLedProjectsPage />,
            handle: { title: "Prowadzone projekty" },
          },
          { path: "projects/:leadershipId", element: <LedProjectPage /> },
          {
            path: "projects/:leadershipId/route-tutorial",
            element: <RouteTutorialPage />,
            handle: { title: "Instrukcja warstw DXF" },
          },
          {
            path: "todo",
            element: <ContractorTodoPage />,
            handle: { title: "Moje terminy" },
          },
          // Urlopy firmy — strona sama odsyła konta inne niż główne (backend też).
          {
            path: "leaves",
            element: <ContractorLeavesPage />,
            handle: { title: "Moje urlopy" },
          },
          {
            path: "invoices",
            element: <ContractorInvoicesPage />,
            handle: { title: "Moje faktury" },
          },
          {
            path: "agreements",
            element: <ContractorAgreementsPage />,
            handle: { title: "Moje umowy" },
          },
          // Zakres współpracy — co firma może u nas robić i gdzie. Read-only;
          // wniosek o rozszerzenie dochodzi w etapie 3.
          {
            path: "scope",
            element: <MyScopePage />,
            handle: { title: "Mój zakres współpracy" },
          },
          {
            path: "proceedings",
            element: <MyProceedingsPage />,
            handle: { title: "Moje postępowania ofertowe" },
          },
          { path: "proceedings/:id", element: <MyProceedingDetailPage /> },
          {
            path: "agreements/:id",
            element: <ContractorAgreementDetailPage />,
          },
          {
            path: "protocols",
            element: <ContractorProtocolsPage />,
            handle: { title: "Moje protokoły POCK" },
          },
          { path: "protocols/:id", element: <ContractorProtocolDetailPage /> },
          {
            path: "weekly-reports",
            element: <WeeklyReportsPage />,
            handle: { title: "Raporty tygodniowe" },
          },
          {
            path: "documentation",
            element: <ClusterListPage />,
            handle: { title: "Dokumentacja" },
          },
          { path: "documentation/:clusterId", element: <ClusterDetailPage /> },
          {
            path: "doc-activity",
            element: <DocActivityPage />,
            handle: { title: "Aktywność dokumentacyjna" },
          },
        ],
      },

      // Documentation clusters — all internal users (backend handles role-based
      // filtering). Kept OUT of TechnicalDirectorGuard so NON-technical directors
      // (e.g. kierownik działu dokumentacji) can reach it; the RoleGuard below is the
      // real access gate.
      {
        path: "documentation",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "employee", "accountant", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <ClusterListPage />,
            handle: { title: "Dokumentacja" },
          },
          { path: ":clusterId", element: <ClusterDetailPage /> },
        ],
      },

      // Requisitions/orders panel — non-technical directors only with the
      // CEO-granted 'Zamawiający' permission (mirrors backend HasOrdersAccess)
      {
        element: <OrdersAccessGuard />,
        children: [
          {
            path: "requisitions",
            element: <RequisitionsListPage />,
            handle: { title: "Zapotrzebowania" },
          },
          {
            path: "requisitions/new",
            element: <RequisitionFormPage />,
            handle: { title: "Nowe zapotrzebowanie" },
          },
          { path: "requisitions/:id", element: <RequisitionDetailPage /> },
          {
            path: "orders",
            element: <OrdersListPage />,
            handle: { title: "Zamówienia" },
          },
          { path: "orders/:id", element: <OrderDetailPage /> },
          {
            path: "protocols",
            element: <ProtocolsListPage />,
            handle: { title: "Protokoły POCK" },
          },
          { path: "protocols/:id", element: <ProtocolDetailPage /> },
        ],
      },

      // Tenders panel — internal users as before; non-technical director only
      // with the CEO-granted 'Przetargi: widok' permission (TendersAccessGuard)
      {
        path: "tenders",
        element: <TendersAccessGuard />,
        children: [
          {
            index: true,
            element: <TendersListPage />,
            handle: { title: "Przetargi" },
          },
          {
            path: "notifications",
            element: <PricingNotificationsPage />,
            handle: { title: "Powiadomienia - przetargi" },
          },
          { path: ":id", element: <TenderDetailPage /> },
          {
            element: <RoleGuard roles={["director", "coordinator"]} />,
            children: [
              {
                path: "new",
                element: <TenderNewPage />,
                handle: { title: "Nowy przetarg" },
              },
            ],
          },
          {
            element: <PricingCatalogGuard />,
            children: [
              {
                path: "pricing-catalog",
                element: <PricingCatalogPage />,
                handle: { title: "Przetargi - katalog cen" },
              },
            ],
          },
          // Konfiguracja odczytu zakresu (P / W / P+W) z prefiksu nazwy przetargu.
          // Zapis jest po stronie backendu tylko dla dyr. technicznego; tutaj wpuszczamy
          // każdego, kto widzi przetargi, bo strona tłumaczy też SKĄD wziął się zakres.
          {
            path: "scope-prefixes",
            element: <TenderScopePrefixesPage />,
            handle: { title: "Przetargi - prefiksy zakresu" },
          },
        ],
      },

      // Technical director only — non-technical directors are redirected to /
      {
        element: <TechnicalDirectorGuard />,
        children: [
          // Projects panel (e) — technical director + coordinator
          {
            path: "projects",
            element: (
              <RoleGuard roles={["director", "coordinator", "accountant"]} />
            ),
            children: [
              {
                index: true,
                element: <ProjectsListPage />,
                handle: { title: "Przedsięwzięcia" },
              },
              {
                path: "new",
                element: <ProjectFormPage />,
                handle: { title: "Nowe przedsięwzięcie" },
              },
              { path: ":id", element: <ProjectDashboardPage /> },
              { path: ":id/edit", element: <ProjectEditPage /> },
              { path: ":id/finance", element: <ProjectFinancePage /> },
              { path: ":id/documentation", element: <ClusterListPage /> },
              {
                path: ":id/documentation/:clusterId",
                element: <ClusterDetailPage />,
              },
              {
                path: ":id/power-project",
                element: <PowerProjectWorkspacePage />,
              },
            ],
          },

          // Clients panel — technical director + coordinator
          {
            path: "clients",
            element: <RoleGuard roles={["director", "coordinator"]} />,
            children: [
              {
                index: true,
                element: <ClientsListPage />,
                handle: { title: "Klienci" },
              },
              { path: ":id", element: <ClientDetailPage /> },
              {
                element: <RoleGuard roles={["director"]} />,
                children: [
                  {
                    path: "new",
                    element: <ClientFormPage />,
                    handle: { title: "Nowy klient" },
                  },
                  { path: ":id/edit", element: <ClientFormPage /> },
                  {
                    path: ":clientId/branches/new",
                    element: <ClientBranchFormPage />,
                    handle: { title: "Nowy oddział klienta" },
                  },
                  {
                    path: ":clientId/branches/:branchId/edit",
                    element: <ClientBranchFormPage />,
                  },
                  {
                    path: ":clientId/branches/:branchId/sub-branches/new",
                    element: <ClientSubBranchFormPage />,
                    handle: { title: "Nowy pododdział klienta" },
                  },
                  {
                    path: ":clientId/branches/:branchId/sub-branches/:subBranchId/edit",
                    element: <ClientSubBranchFormPage />,
                  },
                ],
              },
            ],
          },

          // Pozyskiwanie — firmy, których jeszcze nie mamy w systemie. Cały moduł
          // za jedną flagą; kto ją ma, widzi wszystkie leady i całą historię kontaktu.
          {
            path: "leads",
            element: <LeadsAccessGuard />,
            children: [
              // Lista, zadania i lista blokująca dzielą jedną ramkę z zakładkami —
              // w menu to JEDNA pozycja, adresy zostają osobne dla linkowania.
              {
                element: <LeadsLayout />,
                children: [
                  { index: true, element: <LeadsPage />, handle: { title: "Leady" } },
                  {
                    path: "search",
                    element: <LeadSearchPage />,
                    handle: { title: "Leady — wyszukiwanie" },
                  },
                  {
                    path: "search/:id",
                    element: <LeadSearchDetailPage />,
                    handle: { title: "Leady — kandydaci" },
                  },
                  {
                    path: "campaigns",
                    element: <CampaignsPage />,
                    handle: { title: "Leady — kampanie" },
                  },
                  {
                    path: "campaigns/:id",
                    element: <CampaignDetailPage />,
                    handle: { title: "Leady — kampania" },
                  },
                  {
                    path: "queue",
                    element: <LeadsQueuePage />,
                    handle: { title: "Leady — kolejka wysyłki" },
                  },
                  {
                    path: "funnel",
                    element: <LeadsFunnelPage />,
                    handle: { title: "Leady — lejek" },
                  },
                  {
                    path: "inbox",
                    element: <InboxPage />,
                    handle: { title: "Leady — skrzynka zakupy@" },
                  },
                  { path: "tasks", element: <LeadTasksPage />, handle: { title: "Leady — zadania" } },
                  {
                    path: "do-not-contact",
                    element: <LeadSuppressionPage />,
                    handle: { title: "Leady — nie kontaktować" },
                  },
                ],
              },
              { path: ":id", element: <LeadDetailPage /> },
            ],
          },

          // RFQ — zapytania ofertowe dla podwykonawców. Odczyt dla wewnętrznych,
          // edycja słownika i tak zamknięta na dyrektorze technicznym po stronie API.
          {
            path: "rfq",
            children: [
              {
                path: "categories",
                element: <ScopeCategoriesPage />,
                handle: { title: "Kategorie i zakresy prac" },
              },
              {
                path: "scope-requests",
                element: <ScopeRequestsPage />,
                handle: { title: "Wnioski o rozszerzenie zakresu" },
              },
              {
                path: "declaration-questions",
                element: <DeclarationLibraryPage />,
                handle: { title: "Domyślne oświadczenia" },
              },
              {
                path: "contract-templates",
                element: <ContractTemplatesPage />,
                handle: { title: "Wzory umów — etapy ZE" },
              },
              {
                path: "proceedings",
                element: <ProceedingsPage />,
                handle: { title: "Postępowania ofertowe" },
              },
              { path: "proceedings/:id", element: <ProceedingPanelPage /> },
              {
                path: "account-requests",
                element: <AccountRequestsPage />,
                handle: { title: "Prośby o konto" },
              },
            ],
          },

          // Contractors panel — director + coordinator, plus holders of the
          // 'Podwykonawcy i umowy ramowe' permission
          {
            path: "contractors",
            children: [
              // Megamapa podwykonawców — own guard: also developers, who do not get the
              // rest of the contractors panel. Static segment, so it wins over `:id`.
              {
                path: "megamapa",
                element: <ContractorAreaMapGuard />,
                children: [
                  {
                    index: true,
                    element: <ContractorAreaMapPage />,
                    handle: { title: "Megamapa podwykonawców" },
                  },
                ],
              },
              {
                element: <ContractorsAccessGuard />,
                children: [
                  {
                    index: true,
                    element: <ContractorsListPage />,
                    handle: { title: "Podwykonawcy" },
                  },
                  // Zasady podpowiadania — krąg „Podwykonawcy i umowy ramowe" (CEO, dyrektor
                  // techniczny, flaga), jak backendowa bramka CanManageContractors.
                  {
                    path: "suggestion-rules",
                    element: <ContractorsManageGuard />,
                    children: [
                      {
                        index: true,
                        element: <KnowledgeNotesPage />,
                        handle: { title: "Zasady podpowiadania" },
                      },
                    ],
                  },
                  { path: ":id", element: <ContractorDetailPage /> },
                  {
                    element: <ContractorsManageGuard />,
                    children: [
                      {
                        path: "new",
                        element: <ContractorFormPage />,
                        handle: { title: "Nowy podwykonawca" },
                      },
                      { path: ":id/edit", element: <ContractorFormPage /> },
                    ],
                  },
                ],
              },
            ],
          },

          // Agreements panel — director + coordinator, plus holders of the
          // 'Podwykonawcy i umowy ramowe' permission
          {
            path: "agreements",
            element: <ContractorsAccessGuard />,
            children: [
              {
                index: true,
                element: <AgreementsListPage />,
                handle: { title: "Umowy ramowe" },
              },
              { path: ":id", element: <AgreementDetailPage /> },
            ],
          },

          // Penalties panel — technical director + coordinator
          {
            path: "penalties",
            element: <RoleGuard roles={["director", "coordinator"]} />,
            children: [
              {
                index: true,
                element: <PenaltiesPage />,
                handle: { title: "Kary umowne" },
              },
            ],
          },

          // Weekly reports panel — technical director + coordinator
          {
            path: "weekly-reports",
            element: <RoleGuard roles={["director", "coordinator"]} />,
            children: [
              {
                index: true,
                element: <WeeklyReportsPage />,
                handle: { title: "Raporty tygodniowe" },
              },
            ],
          },

          // Doc activity — director only (backend enforces director-only access)
          {
            path: "doc-activity",
            element: <RoleGuard roles={["director"]} />,
            children: [
              {
                index: true,
                element: <DesignerActivityPage />,
                handle: { title: "Aktywność projektantów" },
              },
            ],
          },

          // Accounting panel (d) — technical director + accountant
          {
            path: "accounting",
            element: <RoleGuard roles={["accountant", "director"]} />,
            children: [
              { index: true, element: <Navigate to="invoices" replace /> },
              {
                path: "invoices",
                element: <AccountingInvoicesPage />,
                handle: { title: "Księgowość – faktury" },
              },
            ],
          },

          // Group finance panel — accountant OR director+is_finance_panel
          {
            path: "group-finance",
            element: <FinancePanelGuard />,
            children: [
              {
                index: true,
                element: <GroupFinancePage />,
                handle: { title: "Finanse grupy" },
              },
            ],
          },

          // CEO access management panel — CEO only
          {
            path: "ceo",
            children: [
              {
                index: true,
                element: (
                  <CEOGuard>
                    <CEOAccessPage />
                  </CEOGuard>
                ),
                handle: { title: "Panel CEO" },
              },
              {
                path: "employee-agreements",
                element: (
                  <CEOGuard>
                    <EmployeeAgreementsListPage />
                  </CEOGuard>
                ),
                handle: { title: "Umowy GKE - pracownicy" },
              },
              {
                path: "employee-agreements/:id",
                element: <EmployeeAgreementDetailPage />,
              },
              {
                path: "billing-overview",
                element: <EmployeeBillingOverviewPage />,
                handle: { title: "Rozliczenia pracowników" },
              },
              {
                path: "aron-expenses",
                element: (
                  <CEOGuard>
                    <AronExpensesPage />
                  </CEOGuard>
                ),
                handle: { title: "Wydatki ARON" },
              },
              {
                path: "aron-knowledge",
                element: (
                  <CEOGuard>
                    <AronKnowledgePage />
                  </CEOGuard>
                ),
                handle: { title: "Baza wiedzy ARON" },
              },
              {
                path: "aron-meta",
                element: (
                  <CEOGuard>
                    <AronMetaPage />
                  </CEOGuard>
                ),
                handle: { title: "META-ARON" },
              },
              {
                path: "aron-mail-aliases",
                element: (
                  <CEOGuard>
                    <AronMailAliasesPage />
                  </CEOGuard>
                ),
                handle: { title: "Aliasy poczty ARON" },
              },
              {
                path: "aron-audit",
                element: (
                  <CEODevGuard>
                    <AronAuditPage />
                  </CEODevGuard>
                ),
                handle: { title: "Audyt ARON" },
              },
            ],
          },

          // Signatory: my billing periods (any internal user with active UR)
          {
            path: "my-billing",
            element: <MyBillingPeriodsPage />,
            handle: { title: "Moje rozliczenia" },
          },

          // Background tasks triggered by the current user (any authenticated user)
          {
            path: "bg-tasks",
            element: <MyBgTasksPage />,
            handle: { title: "Zadania w tle" },
          },

          // Dev panels — developer/technical director only (pages handle redirect)
          {
            path: "dev",
            children: [
              {
                path: "metrics",
                element: <DevMetricsPage />,
                handle: { title: "Dev – metryki" },
              },
              {
                path: "activity",
                element: <DevActivityPage />,
                handle: { title: "Dev – aktywność" },
              },
              {
                path: "accounts",
                element: <DevAccountsPage />,
                handle: { title: "Dev – konta" },
              },
              {
                path: "bg-tasks",
                element: <BgTasksPage />,
                handle: { title: "Dev – zadania w tle" },
              },
              {
                path: "maintenance",
                element: <MaintenanceManagePage />,
                handle: { title: "Dev – maintenance" },
              },
              {
                path: "credentials",
                element: <CredentialsPage />,
                handle: { title: "Dev – credentials" },
              },
              {
                path: "file-scans",
                element: <FileScansPage />,
                handle: { title: "Dev – skanowanie plików" },
              },
              {
                path: "preset-functions",
                element: <PresetFunctionsDevPage />,
                handle: { title: "Dev – funkcje wycen" },
              },
              {
                path: "extensions",
                element: <ExtensionsPage />,
                handle: { title: "Dev – wtyczki" },
              },
              {
                path: "sms",
                element: <SmsLogPage />,
                handle: { title: "Dev – SMS" },
              },
            ],
          },
        ],
      },

      // Działki — globalna lista działek ze wszystkich projektów ZE z filtrami (default:
      // prowadzący = zalogowany). Poza grupą „power-projects", bo otwiera ją także minimalny
      // dostęp do DLC_01 (dlc_01_min) — sam ten widok, bez wejścia w projekty. Statyczna
      // ścieżka wygrywa ranking z "power-projects/:id".
      {
        path: "power-projects/dzialki",
        element: <DLCModuleGuard module={["dlc_01", "dlc_01_min"]} />,
        children: [
          {
            index: true,
            element: <GlobalParcelsPage />,
            handle: { title: "Działki" },
          },
        ],
      },

      // Power projects portfolio (DLC_01) — accessible to any internal user with dlc_01 module
      {
        path: "power-projects",
        element: <DLCModuleGuard module="dlc_01" />,
        children: [
          {
            index: true,
            element: <PowerProjectsPortfolioPage />,
            handle: { title: "Projekty nN/SN" },
          },
          {
            // ZE Megamapa — portfolio-wide map, technical director + (self-gated in the page,
            // enforced by the backend IsTechnicalDirector). Declared before ":id" so the static
            // segment wins over the project-id route.
            path: "megamapa",
            element: <ZEMegaMapPage />,
            handle: { title: "ZE Megamapa" },
          },
          {
            // Podgląd „oczami podwykonawcy" — wewnętrzni oglądają dokładnie tę mapę,
            // którą widzi wskazana firma (backend: as_contractor za bramką modułu).
            path: "contractor-preview/:previewContractorId",
            element: <ContractorMapPage />,
            handle: { title: "Podgląd mapy podwykonawcy" },
          },
          {
            path: "contractor-preview/:previewContractorId/:connectionId",
            element: <ContractorProjectMapPage />,
          },
          {
            // Projekt ZE adresowany id PROFILU (ZEShortTypeProject), nie przedsięwzięcia —
            // odsyłacze ARON-a. Przekierowuje na ":id" (id przedsięwzięcia).
            path: "profil/:connectionId",
            element: <ConnectionRedirectPage />,
          },
          {
            // Widok nadzorczy projektu przekazanego podwykonawcy — ten sam ekran co u firmy,
            // z dodatkami „tylko ELEKTIM". Statyczny segment przed ":id".
            path: "led/:leadershipId",
            element: <LedProjectPage />,
            handle: { title: "Projekt prowadzony przez podwykonawcę" },
          },
          {
            path: "led/:leadershipId/route-tutorial",
            element: <RouteTutorialPage />,
            handle: { title: "Instrukcja warstw DXF" },
          },
          {
            // Doradca AI — nadzór/analityka (dyr. tech.+, backend IsTechnicalDirector).
            path: "doradca",
            element: <AdvisoryOversightPage />,
            handle: { title: "Doradca AI — nadzór" },
          },
          {
            // Kontrola procesu — Moje TODO, Zadania KPI i Arkusz AI jako zakładki jednej
            // strony (?tab=todo|kpi|arkusz-ai). Dostępne każdemu użytkownikowi ZE; backend
            // scope'uje TODO/KPI (dyr. tech./CEO → wszyscy, reszta → self), Arkusz AI tylko
            // dyr. tech. + CEO. Statyczny segment przed ":id", żeby wygrał z trasą projektu.
            path: "kontrola",
            element: <ProcessControlPage />,
            handle: { title: "Kontrola procesu" },
          },
          {
            // Stare adresy trzech widoków prowadzą do właściwej zakładki (z parametrami).
            path: "moje-todo",
            element: <RedirectWithSearch to="/power-projects/kontrola?tab=todo" />,
          },
          {
            path: "kpi",
            element: <RedirectWithSearch to="/power-projects/kontrola?tab=kpi" />,
          },
          {
            path: "arkusz-ai",
            element: <RedirectWithSearch to="/power-projects/kontrola?tab=arkusz-ai" />,
          },
          {
            // Analiza postępów — rozliczenie jednej osoby względem wybranej chwili.
            // Widok dyrekcji technicznej / CEO (backend zwraca 403 pozostałym). Statyczny
            // segment przed ":id", żeby wygrał z trasą projektu.
            path: "kpi/analiza",
            element: <KpiChangeAnalysisPage />,
            handle: { title: "Analiza postępów" },
          },
          {
            // Skrzynka eop@ — korespondencja z ENERGA. Wewnątrz DLC_01, ale za
            // WŁASNĄ bramką: dostęp do modułu ZE to jeszcze nie prawo do czytania
            // poczty operatorskiej. Statyczny segment przed ":id".
            path: "poczta-eop",
            element: <EopMailGuard />,
            children: [
              {
                index: true,
                element: <EopMailboxPage />,
                handle: { title: "Poczta EOP" },
              },
            ],
          },
          {
            // Zamawianie materiałów — wypisy EGiB, mapy zasadnicze, wypisy MPZP i MDCP
            // jako zakładki jednej strony (?tab=wypisy|mz|mpzp|mdcp). Strony szczegółu
            // zamówień (/wypisy/:id, /mz/:id, /mpzp/:id) zostają pod starymi adresami.
            // Static segments before ":id" so they win over the project-id route.
            path: "zamawianie",
            element: <MaterialsOrderingPage />,
            handle: { title: "Zamawianie materiałów" },
          },
          {
            // Ponaglenia — kalendarz telefonów kontrolnych do urzędów i podwykonawców
            // (sprawy złożone/wysłane ze wszystkich rejestrów DLC_01).
            path: "ponaglenia",
            element: <ControlCallsPage />,
            handle: { title: "Ponaglenia" },
          },
          {
            // Stare adresy list prowadzą do właściwej zakładki (z parametrami, np.
            // ?connection= zawężające MPZP do jednego projektu).
            path: "wypisy",
            element: <RedirectWithSearch to="/power-projects/zamawianie?tab=wypisy" />,
          },
          {
            path: "mz",
            element: <RedirectWithSearch to="/power-projects/zamawianie?tab=mz" />,
          },
          {
            path: "mpzp",
            element: <RedirectWithSearch to="/power-projects/zamawianie?tab=mpzp" />,
          },
          {
            path: "wypisy/:orderId",
            element: <WypisOrderPage />,
            handle: { title: "Zamówienie wypisów" },
          },
          {
            path: "mz/:orderId",
            element: <MzOrderPage />,
            handle: { title: "Zamówienie mapy zasadniczej" },
          },
          {
            path: "mpzp/:orderId",
            element: <MpzpOrderPage />,
            handle: { title: "Zamówienie wypisu i wyrysu z MPZP" },
          },
          {
            // Preanaliza dokumentów przysłanych przez urzędy — „czego to dotyczy?"
            // (urzędy rzadko oznaczają odpowiedź numerem naszego wniosku).
            path: "dokumenty-z-urzedow",
            element: <OfficeDocsPage />,
            handle: { title: "Dokumenty z urzędów" },
          },
          {
            // Wnioski urzędowe — silnik pakietów (lokalizacyjne/klauzule/WPW/odrolnienia/ULICP).
            path: "wnioski",
            element: <WnioskiHubPage />,
            handle: { title: "Wnioski urzędowe" },
          },
          {
            // Kolejka dyrektora technicznego: wnioski przekazane do akceptacji ze
            // wszystkich projektów. Statyczny segment wygrywa z :applicationId niżej.
            // Obieg wyłączony (APPLICATION_APPROVAL_ENABLED) → stary link wraca na listę.
            path: "wnioski/do-akceptacji",
            element: APPLICATION_APPROVAL_ENABLED
              ? <WnioskiApprovalQueuePage />
              : <Navigate to="/power-projects/wnioski" replace />,
            handle: { title: "Wnioski do akceptacji" },
          },
          {
            path: "wnioski/:applicationId",
            element: <ApplicationPackagePage />,
            handle: { title: "Pakiet wniosku" },
          },
          {
            path: "pelnomocnictwa",
            element: <PoaRegistryPage />,
            handle: { title: "Rejestr pełnomocnictw" },
          },
          {
            path: ":id",
            element: <PowerProjectWorkspacePage />,
            handle: { title: "Projekt ZE" },
          },
          {
            path: ":id/formatki/:sheetSetId",
            element: <FormatkiPlanPage />,
            handle: { title: "Formatki — rozplanowanie arkuszy" },
          },
          {
            // Masowe pakiety WPW (formularze ENERGA) per zamówienie WPW.
            path: ":id/wpw-masowe/:orderId",
            element: <WpwMassPackagePage />,
            handle: { title: "Masowe pakiety WPW (ENERGA)" },
          },
          {
            // Sekwencja masowa WPW BEZ zlecenia (order-free): pakiety → opisy →
            // formularze → kompilacja → utwórz zlecenie (krok 5).
            path: ":id/wpw-sekwencja",
            element: <WpwMassPackagePage />,
            handle: { title: "Sekwencja masowa WPW" },
          },
          {
            // Szablony formatek są sekcją „Generacji dokumentów" — stary adres
            // (linki w zakładkach/notatkach) prowadzi do właściwej zakładki.
            path: "formatka-templates",
            element: <Navigate to="/doc-generation?tab=formatki" replace />,
          },
          {
            // Presety modułu ZE (warstwy DXF + metadane) — jedna strona z zakładkami.
            path: "presety",
            element: <PresetsPage />,
            handle: { title: "Presety" },
          },
          {
            // Stare adresy obu presetów prowadzą do właściwej zakładki.
            path: "dxf-presets",
            element: <Navigate to="/power-projects/presety" replace />,
          },
          {
            path: "metadata-presets",
            element: <Navigate to="/power-projects/presety?tab=metadane" replace />,
          },
          {
            // Rejestr „szczególnej opieki" (karty specyfiki klientów/oddziałów).
            path: "opieka",
            element: <CareRegistryPage />,
            handle: { title: "Szczególna opieka" },
          },
          {
            path: "designers",
            element: <DesignersPage />,
            handle: { title: "Projektanci" },
          },
          {
            path: "contacts",
            element: <ContactsPage />,
            handle: { title: "Książka telefoniczna" },
          },
        ],
      },
      // Generacja dokumentów (DLC_01) — szablony ODF + anonimizacja PDF (AI)
      {
        path: "doc-generation",
        element: <DLCModuleGuard module="dlc_01" />,
        children: [
          {
            index: true,
            element: <DocGenerationPage />,
            handle: { title: "Generacja dokumentów" },
          },
        ],
      },
      // Przetargi ZE (DLC_01) — bid-stage online drawing
      {
        path: "ze-tenders",
        element: <DLCModuleGuard module="dlc_01" />,
        children: [
          {
            index: true,
            element: <ZETendersPortfolioPage />,
            handle: { title: "Przetargi ZE" },
          },
          {
            path: "eml-import",
            element: <EmlImportPage />,
            handle: { title: "Import EML" },
          },
          {
            path: "capabilities",
            element: <ZECapabilitiesPage />,
            handle: { title: "Uprawnienia ENERGA" },
          },
          {
            path: "analiza",
            element: <ZETenderAnalyticsGuard />,
            children: [
              {
                index: true,
                element: <ZETenderAnalyticsPage />,
                handle: { title: "Przetargi ZE Analityka" },
              },
            ],
          },
          {
            path: ":id",
            element: <ZETenderWorkspacePage />,
            handle: { title: "Przetarg ZE" },
          },
        ],
      },
      // Obieg dokumentów (DLC_02)
      {
        path: "paper-send",
        children: [
          {
            element: <DLCModuleGuard module="dlc_02" />,
            children: [
              {
                element: <CorrespondenceRegisterGuard />,
                children: [
                  {
                    index: true,
                    element: <PaperSendPage />,
                    handle: { title: "Wysyłka papierowa" },
                  },
                ],
              },
              {
                path: "mine",
                element: <PaperSendPage mineOnly />,
                handle: { title: "Moja korespondencja" },
              },
              {
                path: "print-orders",
                element: <PrintOrdersPage />,
                handle: { title: "Zlecenia wydruku" },
              },
            ],
          },
          // Rejestr urzędów mieszka pod /paper-send/, ale NIE należy do DLC_02: ten sam
          // wpis jest adresatem wniosku (DLC_01), odbiorcą przesyłki (DLC_02) i adresem
          // e-Doręczeń (DLC_09). Za bramką korespondencji nie mógł go poprawić nikt, kto
          // literówkę widzi — czyli osoba wypełniająca wniosek przy działce.
          {
            path: "offices",
            element: <AnyDLCModuleGuard />,
            children: [
              {
                index: true,
                element: <OfficesPage />,
                handle: { title: "Urzędy" },
              },
            ],
          },
        ],
      },

      // e-Doręczenia (DLC_09) — kolejka wysyłki dla wszystkich z modułem; skrzynka
      // odbiorcza i konfiguracja skrzynek wyłącznie dla CEO (bramka też po stronie API).
      {
        path: "edoreczenia",
        element: <DLCModuleGuard module="dlc_09" />,
        children: [
          {
            index: true,
            element: <EdoreczeniaOutboxPage />,
            handle: { title: "Kolejka e-Doręczeń" },
          },
          {
            element: <CeoOnlyGuard />,
            children: [
              {
                path: "skrzynka",
                element: <EdoreczeniaInboxPage />,
                handle: { title: "Skrzynka odbiorcza" },
              },
              {
                path: "skrzynki",
                element: <EdoreczeniaMailboxesPage />,
                handle: { title: "Skrzynki i certyfikaty" },
              },
            ],
          },
        ],
      },

      // Narzędzia PDF (DLC_12) — moduł nadawany w panelu CEO; dyrektorzy techniczni
      // i CEO mają go z urzędu. Przełącznik „dane z 5-tki” w czacie asystenta
      // pilnuje się sam (wymaga modułu ARON) — po stronie backendu.
      {
        path: "pdf",
        element: <DLCModuleGuard module="dlc_12" />,
        children: [
          {
            index: true,
            element: <PdfToolsPage />,
            handle: { title: "Narzędzia PDF" },
          },
        ],
      },

      // Obliczenia techniczne (DLC_10) — obliczenia sił na słupach linii nN.
      {
        path: "tech-calc",
        element: <DLCModuleGuard module="dlc_10" />,
        children: [
          {
            index: true,
            element: <TechCalcProjectsPage />,
            handle: { title: "Obliczenia techniczne" },
          },
          {
            // Static segment before ":projectId" so it wins over the project route.
            path: "norms",
            element: <TechCalcNormsGuard />,
            children: [
              {
                index: true,
                element: <NormCatalogPage />,
                handle: { title: "Katalog norm" },
              },
            ],
          },
          {
            path: ":projectId",
            element: <TechCalcWorkspacePage />,
            handle: { title: "Obliczenia techniczne" },
          },
          {
            path: ":projectId/card/:variantId/:poleRef",
            element: <TechCalcCardPage />,
            handle: { title: "Karta obliczeń" },
          },
        ],
      },

      // ARON — AI assistant (DLC_03) — any internal user with the dlc_03 module
      {
        path: "aron",
        element: <DLCModuleGuard module="dlc_03" />,
        children: [
          { index: true, element: <AronPage />, handle: { title: "ARON" } },
          {
            path: "tasks",
            element: <AronScheduledTasksPage />,
            handle: { title: "Zadania cykliczne" },
          },
          {
            path: "mail",
            element: <AronMailPage />,
            handle: { title: "Poczta ARON" },
          },
        ],
      },

      // ENG-ARON — AI design reviewer (DLC_04) — any internal user with the dlc_04 module
      {
        path: "eng-aron",
        element: <DLCModuleGuard module="dlc_04" />,
        children: [
          {
            index: true,
            element: <EngAronReviewsPage />,
            handle: { title: "Recenzje opracowań" },
          },
          {
            path: "reviews/:reviewId",
            element: <EngAronReviewDetailPage />,
            handle: { title: "Recenzja opracowania" },
          },
          {
            path: "knowledge",
            element: <EngAronKnowledgePage />,
            handle: { title: "Baza wiedzy ENG-ARON" },
          },
          {
            path: "findings",
            element: <EngAronFindingsPage />,
            handle: { title: "Historia uwag" },
          },
          {
            path: "chat",
            element: <EngAronChatPage />,
            handle: { title: "Czat ENG-ARON" },
          },
        ],
      },

      // FIN-ARON — finanse projektów ZE (DLC_06): koordynatorzy × pensje × marże + doradca AI.
      // Dane pensyjne — wyłącznie grant dlc_06 (CEO zawsze, podwykonawcy nigdy).
      {
        path: "fin-aron",
        element: <DLCModuleGuard module="dlc_06" />,
        children: [
          {
            index: true,
            element: <FinAronDashboardPage />,
            handle: { title: "FIN-ARON — koordynatorzy × marże" },
          },
          {
            path: "person/:userId",
            element: <FinAronPersonPage />,
            handle: { title: "FIN-ARON — koordynator" },
          },
          {
            path: "overhead",
            element: <FinAronOverheadPage />,
            handle: { title: "FIN-ARON — narzuty" },
          },
          {
            path: "advisor",
            element: <FinAronAdvisorPage />,
            handle: { title: "FIN-ARON — rekomendacje AI" },
          },
          {
            path: "knowledge",
            element: <FinAronKnowledgePage />,
            handle: { title: "Baza wiedzy FIN-ARON" },
          },
        ],
      },

      // EZ-ARON — analiza przetargów BZP (DLC_07): feed okazji + bramka wydatkowa AI.
      {
        path: "ezamowienia",
        element: <DLCModuleGuard module="dlc_07" />,
        children: [
          {
            index: true,
            element: <EzFeedPage />,
            handle: { title: "EZ-ARON — Oferty przetargowe" },
          },
          {
            path: "candidates",
            element: <EzCandidatesPage />,
            handle: { title: "EZ-ARON — kandydaci do analizy AI" },
          },
          {
            path: "benchmarks",
            element: <EzBenchmarksPage />,
            handle: { title: "EZ-ARON — benchmarki cen" },
          },
          {
            path: "chat",
            element: <EzChatPage />,
            handle: { title: "Czat EZ-ARON" },
          },
          {
            path: "p/:id",
            element: <EzProceedingPage />,
            handle: { title: "EZ-ARON — postępowanie" },
          },
          {
            path: "knowledge",
            element: <EzKnowledgePage />,
            handle: { title: "Baza wiedzy EZ-ARON" },
          },
        ],
      },

      // Estimating — kosztorys nakładczy → wartość przetargowa (any internal user)
      {
        path: "estimating",
        element: (
          <RoleGuard
            roles={["director", "coordinator", "accountant", "employee", "hr"]}
          />
        ),
        children: [
          {
            index: true,
            element: <EstimateUploadPage />,
            handle: { title: "Wycena kosztorysu" },
          },
          {
            path: "knowledge",
            element: <EstimateKnowledgePage />,
            handle: { title: "Baza wiedzy PRZ-ARON" },
          },
          {
            path: "custom-prices",
            element: <CustomPricesPage />,
            handle: { title: "Nasza baza cen" },
          },
          {
            path: "catalogs",
            element: <CatalogBrowserPage />,
            handle: { title: "Katalogi i cenniki" },
          },
          {
            path: "takeoff-rules",
            element: <TakeoffRulesPage />,
            handle: { title: "Reguły obmiaru" },
          },
          {
            path: ":jobId",
            element: <EstimateResultPage />,
            handle: { title: "Wynik wyceny" },
          },
        ],
      },

      // User settings — all authenticated users
      {
        path: "settings",
        element: <SettingsPage />,
        handle: { title: "Ustawienia" },
      },
    ],
  },
  {
    path: "maintenance",
    element: <MaintenancePage />,
    handle: { title: "Przerwa techniczna" },
  },
  {
    path: "*",
    element: <NotFoundPage />,
    errorElement: <ErrorPage />,
    handle: { title: "Nie znaleziono" },
  },
]);
